import * as React from "react";

import {
  PlpSelectedFacetOption,
  PlpSortingOptions,
  PresentationSortingOption,
} from "../../model";

import { FacetUrlCache } from "../plp-data-target-url-state";

export interface ProductListState {
  pageSize: number;
  startPage: number;
  selectedSortingOption: PresentationSortingOption;
  facetDisplayNames: FacetUrlCache;
  selectedOptions: PlpSelectedFacetOption[];
}

interface ProductListStateConsumerInterface extends ProductListState {}

interface ProductListStateConsumer_ControllerInterface {
  setStartPage: React.Dispatch<React.SetStateAction<number>>;
  setSelectedSortingOption: React.Dispatch<
    React.SetStateAction<PresentationSortingOption>
  >;
  setSelectedOptions: React.Dispatch<
    React.SetStateAction<PlpSelectedFacetOption[]>
  >;
  setFacetDisplayNames: React.Dispatch<
    React.SetStateAction<FacetUrlCache>
  >;
}

const defaultValue = {} as unknown as ProductListStateConsumerInterface;
const ProductListStateContext = React.createContext(defaultValue);

const defaultControllerValue =
  {} as unknown as ProductListStateConsumer_ControllerInterface;
const ProductListStateControllerContext = React.createContext(
  defaultControllerValue
);

const pageSize = 45;

export const ProductListStateProvider: React.FunctionComponent = (props) => {
  const [startPage, setStartPage] = React.useState(0);

  const [selectedSortingOption, setSelectedSortingOption] =
    React.useState<PresentationSortingOption>(PlpSortingOptions[0]);

  const [selectedOptions, setSelectedOptions] = React.useState<
    PlpSelectedFacetOption[]
  >([]);

  const [facetDisplayNames, setFacetDisplayNames] =
    React.useState<FacetUrlCache>({});

  const value: ProductListStateConsumerInterface = React.useMemo(
    () => ({
      pageSize,
      startPage,
      selectedSortingOption,
      facetDisplayNames,
      selectedOptions,
    }),
    [
      pageSize,
      startPage,
      selectedSortingOption,
      facetDisplayNames,
      selectedOptions,
    ]
  );
  const controllerValue: ProductListStateConsumer_ControllerInterface =
    React.useMemo(
      () => ({
        setStartPage,
        setSelectedSortingOption,
        setSelectedOptions,
        setFacetDisplayNames,
      }),
      [
        setStartPage,
        setSelectedSortingOption,
        setSelectedOptions,
        setFacetDisplayNames,
      ]
    );
  return (
    <ProductListStateContext.Provider value={value}>
      <ProductListStateControllerContext.Provider value={controllerValue}>
        {props.children || []}
      </ProductListStateControllerContext.Provider>
    </ProductListStateContext.Provider>
  );
};

export const useProductListState = () => {
  const context = React.useContext(ProductListStateContext);

  /**
   * Pattern disabled here on purpose
   */
  // if (context === defaultValue) {
  //   throw new Error("useProductListState must be used within a ProductListStateProvider");
  // }

  /**
   * Instead of doing this, seperate the contexts explicitly
   */
  // const allKeys = Object.keys(context);
  // const keysToKeep = keys<ProductListStateConsumerInterface>() as string[];
  // const keysToOmit = allKeys.filter((key) => !keysToKeep.includes(key));
  // const readonlyConsumerInterface = omitBatch(
  //   context as { [key: string]: any },
  //   keysToOmit
  // ) as unknown as ProductListStateConsumerInterface;

  return context;
};

export const useProductListState_asController = () => {
  const context = React.useContext(ProductListStateControllerContext);

  if (context === defaultControllerValue) {
    throw new Error(
      "useProductListState_asController must be used within a ProductListStateProvider"
    );
  }

  return context;
};
